import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import _partition from 'lodash/partition';
import _maxBy from 'lodash/maxBy';
import _indexOf from 'lodash/indexOf';
import _isEmpty from 'lodash/isEmpty';
import cx from 'classnames';
import { saveUploadcareImageToZola } from 'actions/UploadcareActions';
import { autocropUploadcareFile } from 'components/common/UploadcareWidget/utils/crop';
import { CARD_TYPES } from 'cards/constants/CardTypes';
import { fetchUploadcareFile } from 'components/common/UploadcareWidget/utils/api';
import { getCardOrientation } from 'cards/reducers/pdp/selectors';
import { getSizesPerOrientation } from 'paper/utils/helpers';
import {
  SVG_PATHS,
  SHAPES_WITHOUT_CLIP_PATH,
  SHAPES_WITH_SVG_BLEED,
  SHAPES,
} from 'paper/components/CardOptionsPreview/constants';
import { Link } from '@zola/zola-ui/src/components/Link';
import { openModal } from 'cards/actions/modalActions';
import { getSelectedLayout } from 'cards/selectors/reversePrintingLayouts';
import { LAYOUTS } from 'cards/reducers/reversePrintingLayouts';
import DragAndDrop from '../../../../components/util/DragAndDrop';

// Components
import DraggableTextLayer from './DraggableTextLayer';
import DraggableImageLayer from './DraggableImageLayer';
import ImageLayer from './ImageLayer';
import Bleed from './Bleed';

// Actions
import { actions as projectActions } from '../../../actions/projectActions';

// Styles
import './Preview.less';

// Util
import FeatureFlags from '../../../../util/featureFlags';
import { getCustomizationByUUID } from '../../../selectors/cardProjectSelector';
import {
  getCachedUploadcareFiles,
  getCachedMetadata,
} from '../../../../selectors/uploadcareSelectors';
import { placeCardFilter } from './utils';
import { Fold } from './Fold';

// Constants
const SHIMMER_TYPES = ['shimmer-silver', 'shimmer-gold'];

// TODO: look into making this a pure component, seems to be dragging a little bit
class Preview extends React.Component {
  constructor(props) {
    super(props);
    this.setWrapperRef = this.setWrapperRef.bind(this);
    this.handleClickOutside = this.handleClickOutside.bind(this);
    this.handleImageReorder = this.handleImageReorder.bind(this);
  }

  setWrapperRef(node) {
    this.wrapperRef = node;
  }

  handleClickOutside(e) {
    const { activeField, setActiveField } = this.props;
    const deselectActive = window.matchMedia('(min-width: 769px)').matches;

    // check to make sure event target is a child of preview
    // if it is not, we want to set activeField to null
    if (deselectActive && activeField) {
      // if we dont have an activeField, do nothing
      if (this.wrapperRef && !this.wrapperRef.contains(e.target)) {
        // if target is not a child of outermost div, the event originated
        // outside of Preview, set activeField to null
        setActiveField(null);
      } else if (this.cardRef && e.target.isSameNode(this.cardRef)) {
        // if the event originated inside outermost div but not from on of the
        // text element components (meaning it came from this.cardRef) then set activeField
        // to null
        setActiveField(null);
      }
    }
  }

  handleImageReorder(imageElementA, imageElementB) {
    const {
      cachedMetadata,
      cachedUploadcareFiles,
      copyImageToZola,
      onImageDelete,
      onImageSave,
      setActiveField,
    } = this.props;

    // Deselect the active image element
    setActiveField(null);

    // Get each element's cached uploadcare url
    const zolaImageIds = [imageElementA, imageElementB].map(elem => elem.image_id);
    const uploadcareUrls = zolaImageIds.map(id => {
      const metadata = id && cachedMetadata[id];
      return (metadata && metadata.uploadcare_url) || null;
    });

    // For each uploadcare url, load uploadcare file data
    const filePromises = uploadcareUrls.map(uploadcareUrl => {
      if (!uploadcareUrl) return Promise.resolve(null);

      const cachedUploadcareFile = cachedUploadcareFiles[uploadcareUrl];
      if (cachedUploadcareFile) return Promise.resolve(cachedUploadcareFile);

      return fetchUploadcareFile(uploadcareUrl);
    });

    Promise.all(filePromises).then(([fileA, fileB]) => {
      // Autocrop each valid file for the OTHER image element

      // For every valid Uploadcare file, autocrop for the OTHER element and copy to Zola
      const copyPromiseA = fileA
        ? copyImageToZola(autocropUploadcareFile(fileA, imageElementB))
        : Promise.resolve(null);
      const copyPromiseB = fileB
        ? copyImageToZola(autocropUploadcareFile(fileB, imageElementA))
        : Promise.resolve(null);

      // Save the newly saved Zola images to the OTHER element
      Promise.all([copyPromiseA, copyPromiseB]).then(([zolaImageA, zolaImageB]) => {
        const updatedImages = {};

        // Update imageElementA by assigning it the new image OR clearing it (if the new image is null)
        if (zolaImageB) {
          updatedImages[imageElementA.uuid] = zolaImageB.uuid;
        } else {
          onImageDelete(imageElementA.uuid);
        }

        // Update imageElementB by assigning it the new image OR clearing it (if the new image is null)
        if (zolaImageA) {
          updatedImages[imageElementB.uuid] = zolaImageA.uuid;
        } else {
          onImageDelete(imageElementB.uuid);
        }

        // If either new image is defined, save the customization.
        if (Object.keys(updatedImages).length > 0) {
          onImageSave(updatedImages);
        }
      });
    });
  }

  render() {
    /**
     * TODO:
     * Positioning logic for folded cards is determined by isFoldable prop
     * this results in various terniary expresions for positioning see if
     * a separate component is needed to separate this logic
     */
    const {
      customization,
      activeField,
      activeMenuName,
      cachedMetadata,
      children,
      customizationDeleted,
      customizationValues,
      disableBleed,
      editable,
      elements,
      footerLabel,
      handleChange,
      handleDelete,
      handleTextBlur,
      handleTextChange,
      handleCloneElement,
      hasMultipleCustomizableImages = false,
      imageUploadEnabled,
      infoComponents,
      isFlipped,
      isFoldable,
      isReviewStep,
      onClickAction,
      onImageDelete,
      onImageSave,
      onRemoveElementHeightError,
      onRemoveFieldWidthError,
      onSetElementHeightError,
      onSetFieldWidthError,
      onUpdateField,
      page,
      shape,
      enableEnforceCaps,
      customizationFoilColor,
      backgroundColor = 'none',
      envelopeType,
      previewContainerStyles,
      isGuestEnvelopePreview,
      shouldBlurElement,
      isEnvelopeStepRecipientAddressEditable,
      isPostcard,
      hoverOutline,
      outlineAll,
      previewRef,
      cardRef,
      disableFloatingToolbar,
      pageOrientation,
      customizationSize,
      projectAvailableOptions,
      showModal,
      selectedLayout,
    } = this.props;

    const areNewShapesEnabled = FeatureFlags.get('newPaperShapes');
    const cardTypeSupportsNewShape =
      customization.type === CARD_TYPES.std || customization.type === CARD_TYPES.invitation;
    const size =
      customizationSize ||
      projectAvailableOptions?.size?.options[0]?.value ||
      customization?.variation?.option_values?.size;
    // Apply the text layer above the clipped card preview for card types that support new shapes
    // so that the drag icon isn't cut off
    const applyTextLayerAbove =
      areNewShapesEnabled && cardTypeSupportsNewShape && size !== 'postcard';

    const {
      width,
      height,
      vertical_bleed: verticalBleed,
      horizontal_bleed: horizontalBleed,
      tags,
    } = page;

    const [cardWidthPt, cardHeightPt] = getSizesPerOrientation(
      width,
      height,
      isFoldable,
      pageOrientation
    );
    const borderRadius = shape === 'rounded' ? '52px' : '0px';

    const isShimmerType = envelopeType && SHIMMER_TYPES.includes(envelopeType);

    const serverStyle = {
      position: 'relative',
      top: '0pt',
      width: `${cardWidthPt}pt`,
      height: `${cardHeightPt}pt`,
      borderRadius,
      zIndex: 2,
      marginLeft: '0px',
      backgroundColor: (!isShimmerType && backgroundColor) || 'none',
    };

    const cardLabelStyle = {
      borderBottomLeftRadius: borderRadius,
    };

    // iterate through elements and use values from customizationValues if
    // element is present in customizationValues. These values originate from redux-form
    // during the customization flow and have any changes made by user
    const elementsWithFormValues =
      elements &&
      elements.map(element => {
        if (customizationValues && customizationValues[element.uuid]) {
          return customizationValues[element.uuid];
        }
        return element;
      });
    // partition text elements and image elements since we handle them differently
    const [textElements, imageElements] = _partition(
      elementsWithFormValues,
      element => element.content_type === 'TEXT'
    );

    // we need to find if there are any image elements that have a higher layer than text elements
    // because we articially increase the z-index of text elements according to their y
    // position, so we need to know about any images that are meant to be rendered on top of
    // text so we can increase their z-index so it will be rendered on top of all text elements

    let maxTextElementLayer;
    const maxTextElementByLayer = _maxBy(textElements, 'layer');
    if (maxTextElementByLayer) {
      maxTextElementLayer = maxTextElementByLayer.layer;
    }

    // update the layer property (used for z-index style) of any images that overlay all
    // text elements, this usually occurs with foil cards, but also a few others
    // NOTE: important for us to only do this if there are textElements, if there are
    // no text elements (maxTextElementLayer is undefined) then we just use imageElements unmodified
    const imageElementsWithAdjustedLayer = maxTextElementLayer
      ? imageElements.map(imageElement => {
          if (imageElement.layer > maxTextElementLayer) {
            return { ...imageElement, layer: cardHeightPt * 2 };
          }
          return imageElement;
        })
      : imageElements;

    // Image elements with a linked_element_uuid are assumed to be rsvp/meal option bullet points. If the associated text is deleted, hide the image.
    // Images with excluded_from_printing === true won't be printed, so shouldn't be rendered in the preview.
    const visibleImageElements = imageElementsWithAdjustedLayer.filter(element => {
      const {
        linked_element_uuid: linkedElementUUID,
        excluded_from_printing: excludedFromPrinting,
        overlay,
      } = element;
      const isHiddenOverlay = !!overlay && excludedFromPrinting;
      const hasEmptyLinkedElement =
        linkedElementUUID &&
        customizationValues &&
        customizationValues[linkedElementUUID] &&
        !customizationValues[linkedElementUUID].body;

      return !hasEmptyLinkedElement && !isHiddenOverlay;
    });

    const [selectableImages, visibleImages] = _partition(
      visibleImageElements,
      img => img.tag && img.tag.includes('QR_CODE')
    );

    // flag set on the backend for a page which has had its elements line heights adjusted
    // for a rendering improvement, this is required for backwards compatibility
    const boundingBoxesAdjusted = _indexOf(tags, 'bounding_boxes_adjusted') !== -1;

    const isPlaceCard = customization.type === CARD_TYPES.place;
    const showBleed = !disableBleed && customization.type !== CARD_TYPES.envelope;
    const useSVGBleed = cardTypeSupportsNewShape && SHAPES_WITH_SVG_BLEED.includes(shape);
    const useClipPath = cardTypeSupportsNewShape && !SHAPES_WITHOUT_CLIP_PATH.includes(shape);
    const semiDisable = FeatureFlags.get('cardsReversePrinting')
      ? activeMenuName !== 'detailBack' && customizationDeleted
      : customizationDeleted;

    if (
      FeatureFlags.get('cardsReversePrinting') &&
      size !== 'postcard' &&
      activeMenuName === 'detailBack' &&
      selectedLayout.includes(LAYOUTS.noPrint)
    )
      return (
        <div
          className="customization-preview"
          style={{
            width: `${cardWidthPt}pt`,
            height: `${cardHeightPt}pt`,
            ...previewContainerStyles,
          }}
        >
          <div
            className={cx('card', activeMenuName, 'no-print', {
              'clip-path': useClipPath,
              rounded: shape === SHAPES.rounded,
            })}
          >
            {areNewShapesEnabled && !!shape && useClipPath && (
              <svg width="0" height="0">
                <clipPath id="svgClip" clipPathUnits="objectBoundingBox">
                  <path
                    className={cx({ 'rotate-svg': pageOrientation === 'LANDSCAPE' })}
                    d={SVG_PATHS[shape].clipPath}
                  />
                </clipPath>
              </svg>
            )}
            No print on the back
            <div className="no-print-details">
              Add some magic to the back of your card by choosing one of the suggested layouts in
              the panel{' '}
              <span role="img" aria-label="wand-emoji">
                🪄
              </span>
            </div>
          </div>
        </div>
      );

    return (
      <>
        <div
          className={cx('customization-preview', {
            'review-no-margin-bottom': areNewShapesEnabled,
          })}
          style={{
            width: `${cardWidthPt}pt`,
            height: `${cardHeightPt}pt`,
            ...previewContainerStyles,
          }}
          ref={previewRef}
        >
          {/* The bleed info bubble is placed here so it doesn't get cut off by the svg clip path */}
          {showBleed && editable && (
            <div className="bleed__infoBubble">
              Keep important content inside this line. Content outside will still be printed but may
              be trimmed.{' '}
              <Link role="button" inline onClick={() => showModal()}>
                Learn More
              </Link>
            </div>
          )}
          {children}
          <div
            className={cx('card', activeMenuName, {
              'semi-disable': semiDisable,
              hoverable: onClickAction,
              [envelopeType]: isShimmerType,
              'hover-outline': hoverOutline,
              'box-shadow': !areNewShapesEnabled,
              'clip-path': useClipPath,
            })}
            style={serverStyle}
            onClick={onClickAction}
            ref={cardRef}
          >
            {/* The bleed line has to be within the clipped card wrapper so it doesn't show over clipped shapes */}
            {showBleed && (
              <Bleed
                width={cardWidthPt}
                height={cardHeightPt}
                verticalBleed={verticalBleed}
                horizontalBleed={horizontalBleed}
                isRounded={shape === SHAPES.rounded}
                shape={shape}
                size={customizationSize || projectAvailableOptions?.size?.options[0]?.value}
                orientation={page.orientation}
                useSVGBleed={useSVGBleed}
              />
            )}
            {/* IMPORTANT: Wrap image layers in a full-sized div with 'overflow: hidden' so they don't overflow on cards with rounded corners */}
            {visibleImages.length > 0 && (
              <DragAndDrop>
                <div
                  className={cx('image-layer-wrapper', {
                    'clip-path': areNewShapesEnabled && cardTypeSupportsNewShape,
                  })}
                  style={{
                    position: 'absolute',
                    width: `${cardWidthPt}pt`,
                    height: `${cardHeightPt}pt`,
                    borderRadius,
                  }}
                >
                  {areNewShapesEnabled && !!shape && useClipPath && (
                    <svg width="0" height="0">
                      <clipPath id="svgClip" clipPathUnits="objectBoundingBox">
                        <path
                          className={cx({ 'rotate-svg': pageOrientation === 'LANDSCAPE' })}
                          d={SVG_PATHS[shape].clipPath}
                        />
                      </clipPath>
                    </svg>
                  )}
                  {visibleImages
                    .filter(({ tag }) => placeCardFilter(tag, customization))
                    .map(imageElement => (
                      <ImageLayer
                        key={imageElement.uuid}
                        customizationDeleted={customizationDeleted}
                        imageElement={imageElement}
                        isActive={activeField === imageElement.uuid}
                        linkedElement={
                          (imageElement.linked_element_uuid &&
                            customizationValues?.[imageElement.linked_element_uuid]) ||
                          undefined
                        }
                        controlsEnabled={
                          editable &&
                          hasMultipleCustomizableImages &&
                          imageElement.customizable &&
                          imageUploadEnabled
                        }
                        hasUploadcareImage={
                          !!cachedMetadata[imageElement.image_id] &&
                          !!cachedMetadata[imageElement.image_id].uploadcare_url
                        }
                        allowPlaceholder={
                          editable && imageElement.customizable && imageUploadEnabled
                        }
                        isFoldable={isFoldable}
                        isFlipped={isFlipped}
                        onImageDelete={onImageDelete}
                        onImageSave={onImageSave}
                        onImageReorder={this.handleImageReorder}
                        pageHeight={cardHeightPt}
                        pageWidth={cardWidthPt}
                        pageOrientation={pageOrientation}
                        isGuestEnvelopePreview={isGuestEnvelopePreview}
                        isPostcardReviewStep={
                          FeatureFlags.get('envelopeGuestAddressing') && isPostcard && isReviewStep
                        }
                      />
                    ))}
                </div>
              </DragAndDrop>
            )}
            {!applyTextLayerAbove &&
              textElements
                .filter(({ tag }) => placeCardFilter(tag, customization))
                .map((textElement, index) => (
                  <DraggableTextLayer
                    key={textElement.uuid}
                    index={index}
                    customizationValues={customizationValues}
                    editable={editable && textElement.customizable}
                    textElement={textElement}
                    parentDimensions={{ width: cardWidthPt, height: cardHeightPt }}
                    isActive={activeField === textElement.uuid}
                    customizationDeleted={customizationDeleted}
                    boundingBoxesAdjusted={boundingBoxesAdjusted}
                    onSetFieldWidthError={onSetFieldWidthError}
                    onRemoveFieldWidthError={onRemoveFieldWidthError}
                    onSetElementHeightError={onSetElementHeightError}
                    onRemoveElementHeightError={onRemoveElementHeightError}
                    onUpdateField={onUpdateField}
                    handleChange={handleChange}
                    handleTextBlur={handleTextBlur}
                    handleTextChange={handleTextChange}
                    handleDelete={handleDelete}
                    handleCloneElement={handleCloneElement} // experimental
                    isFoldable={isFoldable}
                    pageOrientation={pageOrientation}
                    enableEnforceCaps={
                      enableEnforceCaps &&
                      ['RECIPIENT_ADDRESS', 'PLACE_CARD_GUEST'].includes(textElement.tag)
                    }
                    customizationFoilColor={customizationFoilColor}
                    isTextVerticalAlignmentEnabled={isPlaceCard} // * For Place Cards, we have to account for the vertical alignment of text elements
                    tag={textElement.tag}
                    shouldBlurElement={shouldBlurElement && textElement.tag === 'RECIPIENT_ADDRESS'}
                    isEnvelopeStepRecipientAddressEditable={isEnvelopeStepRecipientAddressEditable}
                    outlineOnHover={
                      FeatureFlags.get('envelopeGuestAddressing') &&
                      (outlineAll || textElement.tag === 'RECIPIENT_ADDRESS')
                    }
                    isPostcardReviewStep={
                      FeatureFlags.get('envelopeGuestAddressing') && isPostcard && isReviewStep
                    }
                    allowFloatingToolbar={!disableFloatingToolbar}
                  />
                ))}
            {onClickAction && (
              <div className="actions-layer">
                <div className="action action--edit">
                  <div className="action__icon">
                    <i className="zolaicon zolaicon-edit" />
                  </div>
                </div>
              </div>
            )}
            {!_isEmpty(infoComponents) && (
              <div className="info-layer">
                {infoComponents.map((infoComponent, index) => (
                  <div className="info-component-container" key={index}>
                    {infoComponent}
                  </div>
                ))}
              </div>
            )}
            {!areNewShapesEnabled && footerLabel && (
              <div className="card-label-container" style={cardLabelStyle}>
                {footerLabel}
              </div>
            )}
          </div>
          {applyTextLayerAbove &&
            textElements
              .filter(({ tag }) => placeCardFilter(tag, customization))
              .map((textElement, index) => (
                <DraggableTextLayer
                  key={textElement.uuid}
                  index={index}
                  customizationValues={customizationValues}
                  editable={editable && textElement.customizable}
                  textElement={textElement}
                  parentDimensions={{ width: cardWidthPt, height: cardHeightPt }}
                  isActive={activeField === textElement.uuid}
                  customizationDeleted={customizationDeleted}
                  boundingBoxesAdjusted={boundingBoxesAdjusted}
                  onSetFieldWidthError={onSetFieldWidthError}
                  onRemoveFieldWidthError={onRemoveFieldWidthError}
                  onSetElementHeightError={onSetElementHeightError}
                  onRemoveElementHeightError={onRemoveElementHeightError}
                  onUpdateField={onUpdateField}
                  handleChange={handleChange}
                  handleTextBlur={handleTextBlur}
                  handleTextChange={handleTextChange}
                  handleDelete={handleDelete}
                  handleCloneElement={handleCloneElement} // experimental
                  isFoldable={isFoldable}
                  pageOrientation={pageOrientation}
                  enableEnforceCaps={
                    enableEnforceCaps &&
                    ['RECIPIENT_ADDRESS', 'PLACE_CARD_GUEST'].includes(textElement.tag)
                  }
                  customizationFoilColor={customizationFoilColor}
                  isTextVerticalAlignmentEnabled={isPlaceCard} // * For Place Cards, we have to account for the vertical alignment of text elements
                  tag={textElement.tag}
                  shouldBlurElement={shouldBlurElement && textElement.tag === 'RECIPIENT_ADDRESS'}
                  isEnvelopeStepRecipientAddressEditable={isEnvelopeStepRecipientAddressEditable}
                  outlineOnHover={
                    FeatureFlags.get('envelopeGuestAddressing') &&
                    (outlineAll || textElement.tag === 'RECIPIENT_ADDRESS')
                  }
                  isPostcardReviewStep={
                    FeatureFlags.get('envelopeGuestAddressing') && isPostcard && isReviewStep
                  }
                  allowFloatingToolbar={!disableFloatingToolbar}
                />
              ))}
          {selectableImages?.map(imageElement => (
            <DraggableImageLayer
              handleChange={handleChange}
              parentDimensions={{ width: cardWidthPt, height: cardHeightPt }}
              imageElement={imageElement}
              isActive={activeField === imageElement.uuid}
              allowPlaceholder={editable && imageElement.customizable && imageUploadEnabled}
              isReviewStep={isReviewStep}
              editable={editable}
            />
          ))}
          {isFoldable && !isReviewStep && (
            <Fold height={cardHeightPt} width={cardWidthPt} pageOrientation={pageOrientation} />
          )}
        </div>
        {areNewShapesEnabled && footerLabel && (
          <div className="card-label-redesign">{footerLabel}</div>
        )}
      </>
    );
  }
}

Preview.propTypes = {
  customizationUUID: PropTypes.string, // eslint-disable-line react/no-unused-prop-types
  customization: PropTypes.shape({}),
  page: PropTypes.shape({}),
  elements: PropTypes.arrayOf(PropTypes.shape({})),
  editable: PropTypes.bool,
  activeMenuName: PropTypes.string,
  children: PropTypes.element,
  customizationValues: PropTypes.shape({}),
  footerLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  onSetFieldWidthError: PropTypes.func,
  onRemoveFieldWidthError: PropTypes.func,
  onSetElementHeightError: PropTypes.func,
  onRemoveElementHeightError: PropTypes.func,
  onImageDelete: PropTypes.func,
  onImageSave: PropTypes.func,
  setActiveField: PropTypes.func,
  shape: PropTypes.string,
  customizationDeleted: PropTypes.bool,
  disableBleed: PropTypes.bool,
  infoComponents: PropTypes.arrayOf(PropTypes.element),
  onClickAction: PropTypes.func,
  activeField: PropTypes.string,
  onUpdateField: PropTypes.func,
  handleChange: PropTypes.func,
  handleDelete: PropTypes.func,
  hasMultipleCustomizableImages: PropTypes.bool,
  imageUploadEnabled: PropTypes.bool,
  handleTextBlur: PropTypes.func,
  handleTextChange: PropTypes.func,
  isFoldable: PropTypes.bool,
  enableEnforceCaps: PropTypes.bool,
  isFlipped: PropTypes.bool,
  isReviewStep: PropTypes.bool,
  handleCloneElement: PropTypes.func,
  projectAvailableOptions: PropTypes.shape({}),
  backgroundColor: PropTypes.string,
  envelopeType: PropTypes.string,
  previewContainerStyles: PropTypes.shape({}),
  pageOrientation: PropTypes.string,
  selectedLayout: PropTypes.arrayOf(PropTypes.string),
};

const mapStateToProps = (state, ownProps) => ({
  cachedMetadata: getCachedMetadata(state),
  cachedUploadcareFiles: getCachedUploadcareFiles(state),
  customization: getCustomizationByUUID(state, ownProps.customizationUUID),
  pageOrientation: getCardOrientation(state),
  selectedLayout: getSelectedLayout(state),
});

const mapDispatchToProps = dispatch => {
  const setActiveField = name => dispatch(projectActions.setActiveField(name));
  return {
    setActiveField,
    copyImageToZola: (uploadcareFile, cache) =>
      dispatch(saveUploadcareImageToZola(uploadcareFile, cache)),
    showModal: () => dispatch(openModal('BLEED_MODAL', {}, { className: 'bleed__modal' })),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Preview);
